/**
 * Trailblazers (TRL) stalemate-clue types.
 *
 * When a team makes no progress on a node for a while, the server unlocks a
 * sequence of escalating clues that all point at ONE fixed target image (see
 * the feature notes in the plan). This module holds:
 *   - the config shape (game-level defaults + optional per-node override),
 *   - the per-team runtime clue state that rides on {@link TrlTeamProgress},
 *   - the socket request/response/broadcast DTOs.
 *
 * SECURITY: raw answer coordinates never appear here. Only derived-safe values
 * travel to clients — the target's area name, an obfuscated coordinate with a
 * map circle that always contains the real spot, and a distance number.
 */
/** Sensible defaults applied to every node until the admin changes them. */
export const DEFAULT_TRL_STALEMATE_CONFIG = {
    area: { enabled: true, delaySeconds: 480 },
    scanner: { enabled: true, delaySeconds: 720 },
    pinpoint: { enabled: true, delaySeconds: 900, obfuscationMeters: 60 },
    showPlanetLabel: true,
};
/**
 * Upgrade a stored or imported config written before the compass clue was
 * retired: its `compass` settings (enabled + delay) carry over to the `area`
 * clue that took its slot. Anything else is returned unchanged.
 */
export function upgradeTrlStalemateConfig(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return raw;
    const obj = raw;
    if (!('compass' in obj))
        return raw;
    const { compass, ...rest } = obj;
    return 'area' in rest ? rest : { ...rest, area: compass };
}
/**
 * Resolve the effective config for a node: a per-node override wins wholesale;
 * otherwise the game-level defaults; otherwise the built-in defaults. Kept as an
 * all-or-nothing pick (no deep merge) so behaviour is easy to reason about.
 */
export function resolveTrlStalemateConfig(gameConfig, nodeOverride) {
    return nodeOverride ?? gameConfig ?? DEFAULT_TRL_STALEMATE_CONFIG;
}
/**
 * Value-equality for two configs across every persisted field. Used by the
 * per-node editor to decide whether a working copy still matches the game
 * defaults — if so it is stored as `null` (inherit) rather than a frozen
 * override, so future changes to the defaults keep propagating.
 */
export function trlStalemateConfigEquals(a, b) {
    return (a.area.enabled === b.area.enabled &&
        a.area.delaySeconds === b.area.delaySeconds &&
        a.scanner.enabled === b.scanner.enabled &&
        a.scanner.delaySeconds === b.scanner.delaySeconds &&
        a.pinpoint.enabled === b.pinpoint.enabled &&
        a.pinpoint.delaySeconds === b.pinpoint.delaySeconds &&
        a.pinpoint.obfuscationMeters === b.pinpoint.obfuscationMeters &&
        a.showPlanetLabel === b.showPlanetLabel);
}
// ============================================================================
// Runtime clue state (per team, per node — masked from rival teams)
// ============================================================================
/**
 * The three clue kinds, in escalation order.
 *
 * A runtime array rather than a bare union so UI that needs to offer all three —
 * the admin's "reveal clue now" override — iterates the same list the type is
 * derived from, and a fourth kind cannot be added without it appearing there.
 */
export const TRL_STALEMATE_CLUE_KINDS = ['area', 'scanner', 'pinpoint'];
/**
 * The clue kinds ENABLED on one node, in escalation order — the admin's game
 * defaults with the node's override applied.
 *
 * "Enabled" is not "revealed": a player-facing surface shows a column for every
 * enabled clue from the off (reading "Locked" until it unlocks), so a clue
 * landing populates a tile that was already on screen rather than growing a new
 * one. The map bell and the Round Info overlay both resolve their columns from
 * this, so neither can offer a clue the other doesn't.
 */
export function enabledTrlClueKinds(gameConfig, nodeOverride) {
    const config = resolveTrlStalemateConfig(gameConfig, nodeOverride);
    return TRL_STALEMATE_CLUE_KINDS.filter((kind) => config[kind].enabled);
}
